import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { fallanalyseAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { TreeDeciduous, Mail, Lock, ArrowRight, ExternalLink, Eye, EyeOff } from 'lucide-react';
import { trackLoginAttempt } from '@/lib/tracking';

const LoginPage = () => {
  const { login } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const entwurfId = searchParams.get('entwurf');
  const isDienstleister = searchParams.get('as') === 'dienstleister';
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    trackLoginAttempt();

    try {
      const result = await login(formData.email, formData.password);
      // Entwurf dem eingeloggten User zuordnen
      if (entwurfId && result?.user?.id) {
        try {
          await fallanalyseAPI.projektEntwurfZuordnen(entwurfId, result.user.id);
          toast.success(t('toasts.analyseGespeichert'));
        } catch { /* Nicht kritisch */ }
      }
      const defaultRoute = result?.user?.role === 'anbieter' ? '/projekte' : '/dashboard';
      navigate(defaultRoute);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('auth.loginFehler'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-warm-50 flex flex-col" data-testid="login-page">
      {/* Header */}
      <header className="p-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <div className="p-2 rounded-lg bg-primary">
            <TreeDeciduous className="h-5 w-5 text-white" />
          </div>
          <span className="font-serif text-xl font-bold text-primary">ForestHub</span>
        </Link>
        
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          {/* Headline Section */}
          <div className="text-center mb-8">
            <h1 className="font-serif text-2xl md:text-3xl font-bold text-gray-900 mb-2">
              {isDienstleister ? t('app.headlineDienstleister') : t('app.headline')}
            </h1>
            <p className="text-gray-500">
              {t('app.subheadline')}
            </p>
          </div>

          {/* Login Card */}
          <Card className="border-warm-200 shadow-soft">
            <CardContent className="p-6">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-gray-700">{t('auth.email')}</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="ihre@email.de"
                      className="pl-10"
                      data-testid="login-email-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-gray-700">{t('auth.password')}</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      type={showPassword ? "text" : "password"}
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className="pl-10 pr-10"
                      data-testid="login-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      data-testid="login-toggle-password"
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                {/* Primary CTA */}
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-primary hover:bg-primary-600"
                  data-testid="login-submit-btn"
                >
                  {loading ? t('common.loading') : t('nav.login')}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </form>

              {/* Secondary Links */}
              <div className="mt-6 space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <Link 
                    to="/passwort-vergessen" 
                    className="text-gray-500 hover:text-primary transition-colors"
                  >
                    {t('auth.forgotPassword')}
                  </Link>
                  <Link 
                    to={entwurfId ? `/registrieren?entwurf=${entwurfId}` : '/registrieren'}
                    className="text-primary hover:underline font-medium"
                  >
                    {t('auth.registerNow')}
                  </Link>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Secondary & Optional CTAs */}
          <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-4 text-sm">
            <a 
              href="https://foresthub.app" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-gray-500 hover:text-primary transition-colors"
            >
              <ExternalLink className="h-4 w-4" />
              {t('nav.toWebsite')}
            </a>
          </div>

          {/* Hint Line */}
          <p className="mt-8 text-center text-sm text-gray-400">
            {t('app.hintLine')}{' '}
            <a 
              href="https://foresthub.app" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-primary hover:underline"
            >
              foresthub.app
            </a>
          </p>
        </div>
      </main>

      {/* Footer */}
      <footer className="p-4 text-center text-xs text-gray-400">
        © {new Date().getFullYear()} ForestHub · HODEMA edertools AB
      </footer>
    </div>
  );
};

export default LoginPage;
